import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWeightHanging, faShoppingCart, faChartLine, faBoxes,
    faTruck, faCheckCircle, faExclamationTriangle, faBan,
    faFileAlt, faCalendarCheck, faArrowUp, faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const KPIStats = ({ kpis }) => {
    const { t } = useTranslation();
    if (!kpis) return null;

    const kpiStrings = t('dashboard.kpi', { returnObjects: true });

    const sections = [
        {
            title: kpiStrings.sections.todayActivity,
            color: '#0891b2',
            bg: '#ecfeff',
            border: '#a5f3fc',
            cards: [
                { label: kpiStrings.labels.weightDispatched, value: `${parseFloat(kpis.today_weight_tons || 0).toFixed(2)} Ton`, icon: faWeightHanging, sub: kpiStrings.subs.today },
                { label: kpiStrings.labels.ordersPlaced, value: kpis.today_orders ?? 0, icon: faShoppingCart, sub: kpiStrings.subs.today },
                { label: kpiStrings.labels.delivered, value: kpis.delivered_today ?? 0, icon: faCheckCircle, sub: kpiStrings.subs.today },
            ]
        },
        {
            title: kpiStrings.sections.thisMonth,
            color: '#7c3aed',
            bg: '#f5f3ff',
            border: '#ddd6fe',
            cards: [
                { label: kpiStrings.labels.weightDispatched, value: `${parseFloat(kpis.month_weight_tons || 0).toFixed(2)} Ton`, icon: faChartLine, sub: kpiStrings.subs.month, trend: parseFloat(kpis.weight_growth) },
                { label: kpiStrings.labels.orders, value: kpis.month_orders ?? 0, icon: faBoxes, sub: kpiStrings.subs.month },
            ]
        },
        {
            title: kpiStrings.sections.operations,
            color: '#d97706',
            bg: '#fffbeb',
            border: '#fde68a',
            cards: [
                { label: kpiStrings.labels.pendingOrders, value: kpis.pending_orders ?? 0, icon: faFileAlt, sub: kpiStrings.subs.awaitingDispatch },
                { label: kpiStrings.labels.inTransit, value: kpis.in_transit_orders ?? 0, icon: faTruck, sub: kpiStrings.subs.onTheWay },
                { label: kpiStrings.labels.expectedDeliveries, value: kpis.expected_deliveries ?? 0, icon: faCalendarCheck, sub: kpiStrings.subs.incomingToday },
                { label: kpiStrings.labels.pendingPurchaseOrders, value: kpis.pending_purchase_orders ?? 0, icon: faFileAlt, sub: kpiStrings.subs.purchase },
            ]
        },
        {
            title: kpiStrings.sections.inventoryAlerts,
            color: '#dc2626',
            bg: '#fff7f7',
            border: '#fecaca',
            cards: [
                { label: kpiStrings.labels.lowStockAlerts, value: kpis.low_stock_alerts ?? 0, icon: faExclamationTriangle, sub: kpiStrings.subs.belowMinimum },
                { label: kpiStrings.labels.outOfStock, value: Number(kpis.out_of_stock_items ?? 0).toLocaleString('en-IN'), icon: faBan, sub: kpiStrings.subs.itemsUnavailable },
            ]
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {sections.map(section => (
                <div key={section.title}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ width: 3, height: 16, borderRadius: 2, backgroundColor: section.color }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                            {section.title}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {section.cards.map(card => (
                            <div
                                key={card.label}
                                style={{
                                    flex: '1 1 160px',
                                    minWidth: 160,
                                    maxWidth: 260,
                                    backgroundColor: '#ffffff',
                                    border: `1px solid ${section.border}`,
                                    borderRadius: 12,
                                    padding: '14px 16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6,
                                    transition: 'box-shadow 0.15s',
                                    cursor: 'default',
                                }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 16px ${section.border}99`}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3 }}>
                                        {card.label}
                                    </span>
                                    <div style={{
                                        width: 30, height: 30, borderRadius: 8,
                                        backgroundColor: section.bg,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: section.color, fontSize: 13, flexShrink: 0, marginLeft: 6
                                    }}>
                                        <FontAwesomeIcon icon={card.icon} />
                                    </div>
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
                                    {card.value}
                                </div>
                                {card.trend !== undefined && !isNaN(card.trend) ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <FontAwesomeIcon
                                            icon={card.trend >= 0 ? faArrowUp : faArrowDown}
                                            style={{ fontSize: 10, color: card.trend >= 0 ? '#10b981' : '#ef4444' }}
                                        />
                                        <span style={{ fontSize: 12, fontWeight: 600, color: card.trend >= 0 ? '#10b981' : '#ef4444' }}>
                                            {t('dashboard.kpi.trend.vsLastMonth', { value: Math.abs(card.trend).toFixed(1) })}
                                        </span>
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 11, color: '#111827' }}>{card.sub}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default KPIStats;
